import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import type { CustomCharge, CreateCustomCharge, UpdateCustomCharge } from '@estimator/shared';

export function useCustomCharges(areaId: string | undefined) {
  return useQuery({
    queryKey: ['custom-charges', areaId],
    queryFn: async () => {
      const { data } = await apiClient.get<CustomCharge[]>('/custom-charges', {
        params: { area_id: areaId },
      });
      return data;
    },
    enabled: !!areaId,
  });
}

export function useCreateCustomCharge() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateCustomCharge) => {
      const { data } = await apiClient.post<CustomCharge>('/custom-charges', body);
      return data;
    },
    onSuccess: (_data, vars) =>
      queryClient.invalidateQueries({ queryKey: ['custom-charges', vars.area_id] }),
  });
}

export function useUpdateCustomCharge() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: UpdateCustomCharge & { id: string }) => {
      const { data } = await apiClient.patch<CustomCharge>(`/custom-charges/${id}`, body);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['custom-charges'] }),
  });
}

export function useDeleteCustomCharge() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/custom-charges/${id}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['custom-charges'] }),
  });
}
